import React, { useState, useEffect } from 'react'

import styles from '../../TechPartnerDashboard/TechPartnerDashboard.module.scss'
import '../../TechPartnerDashboard/TechPartnerDashboard.scss'

import {
    Grid,
    GridColumn,
    GridColumnMenuCheckboxFilter,
    GridColumnMenuFilter,
    GridColumnMenuFilterUIProps,
    GridRowClickEvent,
    GridSortChangeEvent,
    GridRowProps,
} from '@progress/kendo-react-grid'
import { SortDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query'

import {
    fetchCustomerTypes,
    fetchIndustryClassifications,
} from '../../../features/customer/customerAPI'

import {
    fetchCustomers,
    CustomersSalesData,
    fetchNewCustomerCount,
} from '../../../features/sales/salesAPI'

import { AWCButton, BUTTON_TYPES, FastQuoteInput } from '../../../GlobalComponents/Inputs/Inputs'

import { TechPartnerDashboardDataSectionProps } from '../TechPartnerDashboardSection'
import { HoverIcon } from '../../../GlobalComponents/_icons/HoverIcon'

export const CustomersData = (props: TechPartnerDashboardDataSectionProps) => {
    const [fullIndustryClassificationList, setFullIndustryClassificationList] = useState<string[]>(
        []
    )
    const [fullCustomerTypeList, setFullCustomerTypeList] = useState<string[]>([])

    const [customersDataPayload, setCustomersDataPayload] = useState<CustomersSalesData[]>([])
    const [customersDataTotalsPayload, setCustomersDataTotalsPayload] = useState<
        CustomersSalesData[]
    >([])
    const [showAllCustomers, setShowAllCustomers] = useState<boolean>(false)
    const [newCustomersCount, setNewCustomersCount] = useState<number>(0)

    const initialSort: SortDescriptor[] = []
    const [sort, setSort] = React.useState(initialSort)

    const [customerNameFilter, setCustomerNameFilter] = useState<string>('')
    const [fullCustomerNameFilter, setFullCustomerNameFilter] =
        useState<CompositeFilterDescriptor | null>(null)

    const [customerTypeFilter, setCustomerTypeFilter] = useState<string[]>([''])
    const [fullCustomerTypeFilter, setFullCustomerTypeFilter] =
        useState<CompositeFilterDescriptor | null>(null)

    const [industryClassificationFilter, setIndustryClassificationFilter] = useState<string[]>([''])
    const [fullIndustryClassificationFilter, setFullIndustryClassificationFilter] =
        useState<CompositeFilterDescriptor | null>(null)

    useEffect(() => {
        let dataCustomerTypes: CustomerTypeView
        let dataIndustryClasses: IndustryClassificationView

        async function getCustomerTypes() {
            dataCustomerTypes = await fetchCustomerTypes()
        }

        async function getIndustryClasses() {
            dataIndustryClasses = await fetchIndustryClassifications()
        }

        // run get functions in parallel, but for both to finish to avoid extra renders
        const asyncFunctions = [getCustomerTypes, getIndustryClasses]
        const getFilterData = async () => {
            await Promise.all(
                asyncFunctions.map(async (get) => {
                    await get()
                })
            )
        }

        getFilterData().then(() => {
            if (dataCustomerTypes) setFullCustomerTypeList(dataCustomerTypes.map((c) => c.Name!))
            if (dataIndustryClasses)
                setFullIndustryClassificationList(dataIndustryClasses.map((c) => c.IndustryName!))
        })
    }, [])

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length === 0) return

        props.onLoadingChange(true)

        if (showAllCustomers) getAllCustomerData()
        else getCustomerData()
    }, [
        props.selectedInputs,
        sort,
        customerNameFilter,
        customerTypeFilter,
        industryClassificationFilter,
        showAllCustomers,
    ])

    useEffect(() => {
        props.onTitleBarElementChange &&
            props.onTitleBarElementChange(
                <AWCButton
                    type={
                        (customerNameFilter.length > 0 && customerNameFilter[0]) ||
                        (customerTypeFilter.length > 0 && customerTypeFilter[0]) ||
                        (industryClassificationFilter.length > 0 && industryClassificationFilter[0])
                            ? BUTTON_TYPES.BLUE
                            : BUTTON_TYPES.SECONDARY
                    }
                    onClick={
                        (customerNameFilter.length > 0 && customerNameFilter[0]) ||
                        (customerTypeFilter.length > 0 && customerTypeFilter[0]) ||
                        (industryClassificationFilter.length > 0 && industryClassificationFilter[0])
                            ? () => {
                                  clearFilters()
                              }
                            : () => {}
                    }
                    style={{ width: 150 }}
                >
                    Clear Filters
                </AWCButton>
            )

        // column sort highlighting doesn't display correctly on the export image - hide it
        document
            .querySelectorAll('colgroup col.k-sorted')
            .forEach((e) => e.classList.add('hide-on-export'))

        props.onLoadingChange(false)
    }, [customersDataPayload])

    const clearFilters = () => {
        setCustomerNameFilter('')
        setFullCustomerNameFilter(null)
        setCustomerTypeFilter([''])
        setFullCustomerTypeFilter(null)
        setIndustryClassificationFilter([''])
        setFullIndustryClassificationFilter(null)
    }

    const getCustomerData = () => {
        let dataCustomers: CustomersSalesData[]
        let dataCustomersOtherTotals: CustomersSalesData[]
        let dataCustomersTotals: CustomersSalesData[]
        let dataNewCustomerCount: number

        async function getCustomers(controller: AbortController) {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataCustomers = await fetchCustomers(
                {
                    ...props.selectedInputs,
                    CustomerFilters: {
                        OrderBy: sort,
                        CustomerName: customerNameFilter,
                        CustomerType: customerTypeFilter,
                        IndustryClassification: industryClassificationFilter,
                        CustomersOffset: 'Top20',
                        Totals: false,
                    },
                },
                controller
            )
        }

        async function getCustomersOtherTotals(controller: AbortController) {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataCustomersOtherTotals = await fetchCustomers(
                {
                    ...props.selectedInputs,
                    CustomerFilters: {
                        OrderBy: sort,
                        CustomerName: customerNameFilter,
                        CustomerType: customerTypeFilter,
                        IndustryClassification: industryClassificationFilter,
                        CustomersOffset: 'Other',
                        Totals: true,
                    },
                },
                controller
            )
        }

        async function getCustomersTotals(controller: AbortController) {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataCustomersTotals = await fetchCustomers(
                {
                    ...props.selectedInputs,
                    CustomerFilters: {
                        OrderBy: sort,
                        CustomerName: customerNameFilter,
                        CustomerType: customerTypeFilter,
                        IndustryClassification: industryClassificationFilter,
                        CustomersOffset: '',
                        Totals: true,
                    },
                },
                controller
            )
        }

        async function getNewCustomerCount(controller: AbortController) {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataNewCustomerCount = await fetchNewCustomerCount(props.selectedInputs, controller)
        }

        // run get functions in parallel, but for both to finish to avoid extra renders
        const asyncFunctions = [
            getCustomers,
            getCustomersOtherTotals,
            getCustomersTotals,
            getNewCustomerCount,
        ]
        const callAsyncFunctions = async () => {
            await Promise.all(
                asyncFunctions.map(async (get) => {
                    const controller = new AbortController()
                    await get(controller)
                })
            )
        }

        callAsyncFunctions().then(() => {
            if (dataCustomers && dataCustomersOtherTotals) {
                dataCustomers.push({
                    ...dataCustomersOtherTotals[0],
                    CustomerName: 'Other',
                    CustomerType: '',
                    IndustryClassification: '',
                })
                setCustomersDataPayload(dataCustomers)
                // setCustomersDataOtherTotalsPayload(dataCustomersOtherTotals)
            }

            if (dataCustomersTotals) setCustomersDataTotalsPayload(dataCustomersTotals)
            if (dataNewCustomerCount) setNewCustomersCount(dataNewCustomerCount)
        })
    }

    const getAllCustomerData = async () => {
        let dataCustomers: CustomersSalesData[]

        async function getAllCustomers(controller: AbortController) {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataCustomers = await fetchCustomers(
                {
                    ...props.selectedInputs,
                    CustomerFilters: {
                        OrderBy: sort,
                        CustomerName: customerNameFilter,
                        CustomerType: customerTypeFilter,
                        IndustryClassification: industryClassificationFilter,
                        CustomersOffset: '',
                        Totals: false,
                    },
                },
                controller
            )
        }

        const controller = new AbortController()
        await getAllCustomers(controller).then(() => {
            if (dataCustomers) setCustomersDataPayload(dataCustomers)
        })
    }

    // add secondary note to section title
    useEffect(
        () =>
            props.onTitleBarTextChange &&
            props.onTitleBarTextChange(
                <div className={styles.customersTitle}>
                    <div>New Customers: {newCustomersCount}</div>
                    <div className={styles.customersSecondaryTitle}>
                        ($0 Tech Partner's sales in the past 24 months)
                    </div>
                </div>
            ),
        [newCustomersCount]
    )

    useEffect(() => {
        let tempCustomerName: string = ''

        if (fullCustomerNameFilter && fullCustomerNameFilter.filters.length > 0)
            fullCustomerNameFilter.filters.forEach((filter: any) => {
                tempCustomerName = filter.filters[0].value
            })

        setCustomerNameFilter(tempCustomerName)
    }, [fullCustomerNameFilter])

    useEffect(() => {
        const tempFilters: string[] = []

        if (fullCustomerTypeFilter && fullCustomerTypeFilter.filters.length > 0) {
            fullCustomerTypeFilter.filters.forEach((filter: any) => {
                filter.filters.forEach((f: any) => {
                    tempFilters.push(f.value)
                })
            })
            setCustomerTypeFilter(tempFilters)
        } else {
            setCustomerTypeFilter([''])
        }
    }, [fullCustomerTypeFilter])

    useEffect(() => {
        const tempFilters: string[] = []

        if (
            fullIndustryClassificationFilter &&
            fullIndustryClassificationFilter.filters.length > 0
        ) {
            fullIndustryClassificationFilter.filters.forEach((filter: any) => {
                filter.filters.forEach((f: any) => {
                    tempFilters.push(f.value)
                })
            })
            setIndustryClassificationFilter(tempFilters)
        } else {
            setIndustryClassificationFilter([''])
        }
    }, [fullIndustryClassificationFilter])

    interface TotalRowProps {
        data: CustomersSalesData[]
        field: string
    }

    const MoneyTotalCell = (props: TotalRowProps) => {
        // // totals the data in the grid
        // const total =
        //     props.data.length > 0 && props.data[0].CustomerName !== ''
        //         ? props.data.reduce(
        //               (acc: number, current: CustomersSalesData) =>
        //                   acc + (current[props.field] as number),
        //               0
        //           )
        //         : 0
        // find the total from a separately-queried total payload
        let total = 0
        if (props.data.length > 0 && props.data[0][props.field])
            total = props.data[0][props.field] as number
        return (
            <td className={styles.dataCurrency}>
                {total.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                })}
            </td>
        )
    }
    const PercentTotalCell = (props: TotalRowProps) => {
        // find the percent from a separately-queried total payload
        let total = 0
        if (props.data.length > 0 && props.data[0][props.field])
            total = props.data[0][props.field] as number
        return (
            <td className={styles.dataPercent}>
                {total.toLocaleString('en-US', {
                    style: 'percent',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                })}
            </td>
        )
    }

    const renderCustomersGrid = () => {
        let customerData = getCustomersDataPayload()

        const setCustomSort = (curSort: SortDescriptor[]) => {
            const sortByAscendingFields = ['CustomerName', 'CustomerType', 'IndustryClassification']

            curSort = curSort.map((s) => {
                let newDirection = 'asc'
                if (sortByAscendingFields.indexOf(s.field) === -1 && s.dir === 'asc')
                    newDirection = 'desc'
                return { ...s, dir: newDirection } as SortDescriptor
            })

            return curSort
        }
        const moneyTotalCell = (props: any) => (
            <MoneyTotalCell field={props.field} data={customersDataTotalsPayload} />
        )
        const percentTotalCell = (props: any) => (
            <PercentTotalCell field={props.field} data={customersDataTotalsPayload} />
        )
        const cagrHeader5Year = (props: any) => (
            <span className='k-cell-inner'>
                <span className='k-link'>
                    <span className='k-column-title'>
                        5 Year
                        <HoverIcon
                            style={{ textAlign: 'left' }}
                            growLeft={false}
                            growDown={false}
                            growUp={false}
                            renderIcon={() => <span>&nbsp;CAGR</span>}
                        >
                            {(closeBubble: () => void) => (
                                <div className={styles.identifierBubble} style={{ width: 270 }}>
                                    <div>
                                        When less than 5 years of information is available, the CAGR
                                        is adjusted accordingly.
                                    </div>
                                </div>
                            )}
                        </HoverIcon>
                    </span>
                </span>
            </span>
        )

        const rowRender = (
            trElement: React.ReactElement<HTMLTableRowElement>,
            props: GridRowProps
        ) => {
            if (props.dataItem.CustomerName !== 'Other') return trElement

            return React.cloneElement(
                trElement,
                { className: 'other-row', title: 'Click to expand' },
                trElement.props.children
            )
        }

        return (
            <div className='customers-data-grid'>
                <Grid
                    data={customerData}
                    style={{
                        width: '100%',
                    }}
                    filterable={false}
                    sortable={true}
                    sort={sort}
                    onSortChange={(e: GridSortChangeEvent) => {
                        setSort(setCustomSort(e.sort)) //first sort is DESC
                    }}
                    scrollable={'none'}
                    rowRender={rowRender}
                    onRowClick={(e: GridRowClickEvent) => {
                        e.dataItem.CustomerName == 'Other' && setShowAllCustomers(true)
                    }}
                >
                    <GridColumn
                        title='Company'
                        field='CustomerName'
                        columnMenu={CustomerNameFilterCell}
                        cell={(props: any) => (
                            <td className={`${styles.dataLabel} ${styles.alignLeft}`}>
                                {props.dataItem.CustomerName
                                    ? props.dataItem.CustomerName.replaceAll('||', '|').replaceAll(
                                          '|',
                                          ' - '
                                      )
                                    : ''}
                            </td>
                        )}
                        footerCell={(props: any) => <td className={styles.dataLabel}>Total</td>}
                    />
                    <GridColumn
                        title='YTD Sales'
                        field='YTDSales'
                        headerClassName='dataCurrency'
                        cell={(props: any) => (
                            <td className={styles.dataCurrency}>
                                {props.dataItem.CustomerName
                                    ? props.dataItem.YTDSales?.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                      })
                                    : ''}
                            </td>
                        )}
                        footerCell={moneyTotalCell}
                    />
                    <GridColumn
                        title='PYTD Sales'
                        field='PYTDSales'
                        headerClassName='dataCurrency'
                        cell={(props: any) => (
                            <td className={styles.dataCurrency}>
                                {props.dataItem.CustomerName
                                    ? props.dataItem.PYTDSales?.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                      })
                                    : ''}
                            </td>
                        )}
                        footerCell={moneyTotalCell}
                    />
                    <GridColumn
                        title='2 PYTD Sales'
                        field='P2YTDSales'
                        headerClassName='dataCurrency'
                        cell={(props: any) => (
                            <td className={styles.dataCurrency}>
                                {props.dataItem.CustomerName
                                    ? props.dataItem.P2YTDSales?.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                      })
                                    : ''}
                            </td>
                        )}
                        footerCell={moneyTotalCell}
                    />
                    <GridColumn
                        title='3 PYTD Sales'
                        field='P3YTDSales'
                        headerClassName='dataCurrency'
                        cell={(props: any) => (
                            <td className={styles.dataCurrency}>
                                {props.dataItem.CustomerName
                                    ? props.dataItem.P3YTDSales?.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                      })
                                    : ''}
                            </td>
                        )}
                        footerCell={moneyTotalCell}
                    />
                    <GridColumn
                        title='4 PYTD Sales'
                        field='P4YTDSales'
                        headerClassName='dataCurrency'
                        cell={(props: any) => (
                            <td className={styles.dataCurrency}>
                                {props.dataItem.CustomerName
                                    ? props.dataItem.P4YTDSales?.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                      })
                                    : ''}
                            </td>
                        )}
                        footerCell={moneyTotalCell}
                    />
                    <GridColumn
                        title='5 PYTD Sales'
                        field='P5YTDSales'
                        headerClassName='dataCurrency'
                        cell={(props: any) => (
                            <td className={styles.dataCurrency}>
                                {props.dataItem.CustomerName
                                    ? props.dataItem.P5YTDSales?.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: 'USD',
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                      })
                                    : ''}
                            </td>
                        )}
                        footerCell={moneyTotalCell}
                    />
                    <GridColumn
                        field='P5YearCAGR'
                        headerClassName='dataPercent allowOverflow'
                        cell={(props: any) => (
                            <td
                                className={styles.dataPercent}
                                style={{
                                    color: props.dataItem.P5YearCAGR > 0.025 ? 'green' : 'red',
                                }}
                            >
                                {props.dataItem.CustomerName
                                    ? props.dataItem.P5YearCAGR?.toLocaleString('en-US', {
                                          style: 'percent',
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 2,
                                      })
                                    : ''}
                            </td>
                        )}
                        headerCell={cagrHeader5Year}
                        footerCell={percentTotalCell}
                    />
                    <GridColumn
                        title='Customer Type'
                        field='CustomerType'
                        cell={(props: any) => (
                            <td>
                                {props.dataItem.CustomerName ? props.dataItem.CustomerType : ''}
                            </td>
                        )}
                        columnMenu={CustomerTypeCheckboxFilter}
                    />
                    <GridColumn
                        title='Industry'
                        field='IndustryClassification'
                        cell={(props: any) => (
                            <td>
                                {props.dataItem.CustomerName
                                    ? props.dataItem.IndustryClassification
                                    : ''}
                            </td>
                        )}
                        columnMenu={IndustryClassificationCheckboxFilter}
                    />
                </Grid>
            </div>
        )
    }
    const CustomerNameFilterCell = (props: any) => {
        return (
            <GridColumnMenuFilter
                {...props}
                filterUI={CustomFilterUI}
                expanded={true}
                onFilterChange={(filter: CompositeFilterDescriptor | null) => {
                    setFullCustomerNameFilter(filter)
                }}
            />
        )
    }

    const CustomFilterUI = (props: GridColumnMenuFilterUIProps) => {
        const onChange = (event: any) => {
            const value = event.target.value
            const { firstFilterProps } = props

            firstFilterProps.onChange({
                value,
                operator: 'eq',
                syntheticEvent: event.syntheticEvent,
            })
        }

        return (
            <div style={{ margin: 25, display: 'flex', justifyContent: 'center' }}>
                <input
                    defaultValue={customerNameFilter}
                    onChange={onChange}
                    style={{ width: 170, fontSize: 16, padding: 5, fontWeight: 300 }}
                />
            </div>
        )
    }

    const CustomerTypeCheckboxFilter = (props: any) => {
        return (
            <div>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        fullCustomerTypeList &&
                        fullCustomerTypeList.map((str: string) => {
                            return { CustomerType: str }
                        })
                    }
                    filter={fullCustomerTypeFilter}
                    expanded={true}
                    onFilterChange={(filter: CompositeFilterDescriptor | null) => {
                        setFullCustomerTypeFilter(filter)
                    }}
                />
            </div>
        )
    }

    const IndustryClassificationCheckboxFilter = (props: any) => {
        return (
            <div>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        fullIndustryClassificationList &&
                        fullIndustryClassificationList.map((str: string) => {
                            return { IndustryClassification: str }
                        })
                    }
                    filter={fullIndustryClassificationFilter}
                    expanded={true}
                    onFilterChange={(filter: CompositeFilterDescriptor | null) => {
                        setFullIndustryClassificationFilter(filter)
                    }}
                />
            </div>
        )
    }

    function getCustomersDataPayload() {
        return customersDataPayload.concat(
            new Array(customersDataPayload.length > 4 ? 0 : 5 - customersDataPayload.length).fill({
                CustomerName: '',
                YTDSales: 0,
                PYTDSales: 0,
                P2YTDSales: 0,
                P3YTDSales: 0,
                P4YTDSales: 0,
                P5YTDSales: 0,
                P5YearCAGR: 0,
                CustomerType: '',
                IndustryClassification: '',
            })
        )
    }

    return (
        <div className={`${styles.customers} customers`} id='customers'>
            {renderCustomersGrid()}
        </div>
    )
}

export default CustomersData
